// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
    Box,
    Button,
    Columns,
    Column,
    Flex,
    Stack,
    TextField,
    Select
} from '@locus-taxy/neo-ui';
import { Paths, rankWith, uiTypeIs } from '@jsonforms/core';

import useArrayControlProps from 'platform/settings/form/renderers/util/useArrayControlProps';

type Props = {};

const CallbackListControl = (props: Props): React.Node => {
    const { t } = useTranslation('platform');

    const {
        description,
        data,
        enabled,
        handleChange,
        label,
        path,
        required,
        removeItems,
        schema,
        translationKey
    } = useArrayControlProps(props);

    const {
        key,
        typeKey,
        callbackTypes,
        defaultValue,
        hideTypeSelect
    } = schema;
    // To show a row when data is undefined or [] we add empty value and show a default row.
    const items = data?.length ? data : [defaultValue];

    React.useEffect(() => {
        // Edge case:
        // Whenever we have only default item then remove that from data.
        // This helps avoids the case where we want to remove all callbacks.

        if (data.length === 1) {
            const typeValue = hideTypeSelect ? '' : _.get(data[0], typeKey);
            const callbackUrl = _.get(data[0], key);
            if (!typeValue && !callbackUrl) {
                handleChange(path, []);
            }
        }
    }, [data, handleChange, key, path, typeKey, hideTypeSelect]);

    const getChildPath = (index: number): string => {
        return Paths.compose(path, `${index}`);
    };

    const handleAdd = () => {
        handleChange(path, items.concat([defaultValue]));
    };

    const callbackTypeOptions = React.useMemo(() => {
        return callbackTypes.map(callbackType => {
            return { label: callbackType, value: callbackType };
        });
    }, [callbackTypes]);

    return (
        <Stack space="medium">
            {items.map((item, index) => {
                const childPath = getChildPath(index);
                const typeValue = _.get(item, typeKey);
                const callbackUrl = _.get(item, key);
                const error =
                    required && data?.length && !callbackUrl
                        ? t('settings.common.requiredFieldMessage')
                        : undefined;
                const callbackTypeError =
                    required && data?.length && !typeValue
                        ? t('settings.common.requiredFieldMessage')
                        : undefined;
                const handleTypeChange = (option: {
                    label: string,
                    value: string
                }): void => {
                    const value = option?.value || '';
                    const updatedItem = _.setWith(
                        _.clone(item),
                        typeKey,
                        value,
                        _.clone
                    );
                    handleChange(childPath, updatedItem);
                };

                const handleUrlChange = (value: ?string): void => {
                    const updatedItem = _.setWith(
                        _.clone(item),
                        key,
                        value,
                        _.clone
                    );
                    handleChange(childPath, updatedItem);
                };

                return (
                    <Columns key={childPath} space="small">
                        {!hideTypeSelect && (
                            <Column width="1/4">
                                <Select
                                    required={required}
                                    value={typeValue}
                                    options={callbackTypeOptions}
                                    label={t(`${translationKey}.typeLabel`)}
                                    placeholder={t(
                                        `${translationKey}.typeLabel`
                                    )}
                                    disabled={!enabled}
                                    fullWidth
                                    onChange={handleTypeChange}
                                    error={!!callbackTypeError}
                                    helperText={callbackTypeError}
                                />
                            </Column>
                        )}

                        <Column width="fluid">
                            <TextField
                                label={`${label} ${index + 1}`}
                                value={callbackUrl}
                                fullWidth
                                required={required}
                                error={!!error}
                                disabled={!enabled}
                                helperText={error ?? description}
                                onChange={handleUrlChange}
                            />
                        </Column>
                        {data?.length > 1 && (
                            <Column width="content">
                                <Box mt="2" ml="2">
                                    <Button
                                        disabled={!enabled}
                                        appearance="icon"
                                        color="grey.600"
                                        size="medium"
                                        icon="delete"
                                        onClick={removeItems(path, [index])}
                                    />
                                </Box>
                            </Column>
                        )}
                    </Columns>
                );
            })}

            <Flex alignSelf="flex-end">
                <Button onClick={handleAdd} palette="primary">
                    {t(`${translationKey}.actionLabel`)}
                </Button>
            </Flex>
        </Stack>
    );
};

export default CallbackListControl;

export const callbackListControlTester: any = rankWith(
    1,
    uiTypeIs('CallbackListControl')
);
